import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useUI } from '../../context/UIContext';
import QuickView from './QuickView';

const ProductCard = ({ product }) => {
  const [showQuickView, setShowQuickView] = useState(false);
  const { addToCart } = useCart();
  const { openCart } = useUI();
  const navigate = useNavigate();

  if (!product) return null;

  const productId = product._id;
  const productName = product.name || 'Product';
  const productImage = product.images?.[0] || product.image || 'https://via.placeholder.com/400';
  const productPrice = product.price || 0;
  const productOriginalPrice = product.originalPrice || product.price || 0;
  
  let discountPercent = product.discount;
  if (!discountPercent && productOriginalPrice > productPrice) {
    discountPercent = Math.round(((productOriginalPrice - productPrice) / product.originalPrice) * 100);
  }

  const thumbnails = product.thumbnails?.length ? product.thumbnails : (product.images?.slice(0, 3) || []);
  const sizes = product.variants ? [...new Set(product.variants.map(v => v.size).filter(Boolean))] : (product.sizes || []);
  const sizeList = sizes.map(s => typeof s === 'object' ? s.name : s);
  const leftBadge = product.leftBadge || (product.isNew ? 'NEW' : '');
  const isCompletelyOutOfStock = !product.inStock;
  const rightBadge = isCompletelyOutOfStock ? 'SOLD OUT' : (product.rightBadge || (product.discount > 0 ? 'SALE' : ''));

  const handleAddToCart = () => {
    const defaultVariant = product.variants && product.variants.length > 0 ? product.variants[0] : null;
    if (defaultVariant && !defaultVariant.inStock) {
      setShowQuickView(true);
      return;
    }
    addToCart(product, 1, { size: defaultVariant?.size || '', color: defaultVariant?.color || '' });
    openCart();
  };

  const handleSizeClick = (e, size) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/product/${productId}?size=${encodeURIComponent(size)}`);
  };

  return (
    <div className="product-card relative group w-full h-full flex flex-col" style={{ maxWidth: 'none', margin: 0, minHeight: '380px' }}>
      {leftBadge && <span className="badge-left">{leftBadge}</span>}
      {rightBadge && <span className="badge-right">{rightBadge}</span>}

      <div className="product-image-wrapper relative overflow-hidden aspect-[3/4] bg-gray-50 flex-shrink-0">
        <Link to={`/product/${productId}`}>
          <img src={productImage} alt={productName} className="product-image w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </Link>
        <button
          className="quick-view-btn absolute bottom-4 left-1/2 transform -translate-x-1/2 px-4 py-2 text-sm opacity-0 group-hover:opacity-100 transition duration-300"
          style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}
          onClick={() => setShowQuickView(true)}
        >
          QUICK VIEW
        </button>
      </div>

      {thumbnails.length > 0 && (
        <div className="product-thumbnails flex justify-center gap-2 mt-3 flex-shrink-0">
          {thumbnails.slice(0, 3).map((thumb, idx) => (
            <img key={idx} src={thumb} alt="" className="thumbnail w-12 h-12 object-cover border border-gray-200" />
          ))}
        </div>
      )}

      <div className="product-title-container text-center mt-2 px-1 flex-1 flex flex-col justify-center">
        <h3 className="product-title text-sm font-medium line-clamp-2" style={{ color: 'var(--text-color)' }}>
          {productName}
        </h3>
      </div>

      <div className="product-pricing flex justify-center items-center gap-2 mt-1 pb-2 flex-shrink-0">
        {productOriginalPrice > productPrice && (
          <span className="old-price text-gray-400 line-through text-sm">Rs. {productOriginalPrice.toLocaleString()}</span>
        )}
        <span className="new-price text-green-600 font-semibold">Rs. {productPrice.toLocaleString()}</span>
        {discountPercent > 0 && (
          <span className="save-badge text-white text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--primary-color)' }}>Save {discountPercent}%</span>
        )}
      </div>

      {sizeList.length > 0 && (
        <div className="product-sizes flex justify-center gap-2 flex-wrap mt-2 flex-shrink-0">
          {sizeList.slice(0, 4).map((size, idx) => {
            const hasStock = product.variants?.some(v => v.size === size && v.inStock) ?? true;
            return (
              <span 
                key={idx} 
                className={`size-chip border border-gray-300 px-2 py-0.5 text-xs cursor-pointer hover:border-[var(--primary-color)] hover:text-[var(--primary-color)] transition-colors ${!hasStock ? 'opacity-50 line-through cursor-not-allowed' : ''}`}
                onClick={(e) => {
                  if (hasStock) handleSizeClick(e, size);
                }}
              >
                {size}
              </span>
            );
          })}
        </div>
      )}

      {showQuickView && (
        <QuickView product={product} onClose={() => setShowQuickView(false)} onAddToCart={handleAddToCart} />
      )}
    </div>
  );
};

export default ProductCard;